import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { backendConfig } from '../config/backendConfig';
import ArtmosphereNavbar from '../components/ArtmosphereNavbar';
import { authFetch } from '../utils/authFetch';

export default function OrganizationRenewal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [initialLoading, setInitialLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [existingMentors, setExistingMentors] = useState([]);
  const [selectedMentors, setSelectedMentors] = useState([]);
  const [newMentors, setNewMentors] = useState([]);
  const [currentStep, setCurrentStep] = useState(1); // 1: Select mentors, 2: Choose plan, 3: Checkout
  const [selectedPlan, setSelectedPlan] = useState('mentor-basic');
  const [mentorCount, setMentorCount] = useState(0);

  // Fetch existing mentors for the expired account
  const fetchExistingMentors = useCallback(async () => {
    try {
      setInitialLoading(true);
      console.log('OrganizationRenewal: Fetching existing mentors for user:', user.uid);
      console.log('OrganizationRenewal: Full user object:', user);
      
      const response = await fetch(`${backendConfig.baseUrl}/api/mentors/existing?ownerId=${user.uid}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      console.log('OrganizationRenewal: Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('OrganizationRenewal: API error response:', errorText);
        throw new Error(`Failed to fetch existing mentors: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('OrganizationRenewal: Existing mentors data:', data);
      
      setExistingMentors(data.mentors || []);
      setSelectedMentors(data.mentors || []); // Pre-select all existing mentors
      setMentorCount(data.mentors?.length || 0);
      
      // If no mentors found, show a message but don't treat it as an error
      if (!data.mentors || data.mentors.length === 0) {
        console.log('OrganizationRenewal: No existing mentors found for this account');
      }
    } catch (err) {
      console.error('OrganizationRenewal: Error fetching existing mentors:', err);
      setError(`Error loading existing mentors: ${err.message}`);
    } finally {
      setInitialLoading(false);
    }
  }, [user.uid]);

  useEffect(() => {
    fetchExistingMentors();
  }, [fetchExistingMentors]);

  // Generate mentor plans based on mentor count
  const generateMentorPlans = (mentorCount) => {
    const basePlans = {
      'mentor-basic': { name: 'Basic', basePrice: 999, features: ['Mentor Management Dashboard', 'Co-Learning Space Access', 'Student Progress Tracking', 'Organization Tools'] },
      'mentor-pro': { name: 'Pro', basePrice: 1299, features: ['Everything in Basic', 'Advanced Analytics', 'Custom Reports', 'Priority Support'] },
      'mentor-premium': { name: 'Premium', basePrice: 1599, features: ['Everything in Pro', 'White-label Options', 'API Access', 'Dedicated Support'] }
    };

    const plans = {};
    Object.keys(basePlans).forEach(planKey => {
      const plan = basePlans[planKey];
      const totalPrice = plan.basePrice * mentorCount;
      const perMentorPrice = plan.basePrice;
      
      plans[planKey] = {
        ...plan,
        totalPrice,
        perMentorPrice,
        mentorCount
      };
    });

    return plans;
  };

  const mentorPlans = generateMentorPlans(mentorCount);

  const handleMentorToggle = (mentorId, isSelected) => {
    if (isSelected) {
      setSelectedMentors(prev => [...prev, existingMentors.find(m => m.id === mentorId)]);
    } else {
      setSelectedMentors(prev => prev.filter(m => m.id !== mentorId));
    }
  };

  const handleAddNewMentor = () => {
    setNewMentors(prev => [...prev, { id: Date.now(), name: '', email: '', isNew: true }]);
  };

  const handleRemoveNewMentor = (mentorId) => {
    setNewMentors(prev => prev.filter(m => m.id !== mentorId));
  };

  const handleNewMentorChange = (mentorId, field, value) => {
    setNewMentors(prev => prev.map(m => 
      m.id === mentorId ? { ...m, [field]: value } : m
    ));
  };

  const handleNextStep = () => {
    const totalMentors = selectedMentors.length + newMentors.length;
    setMentorCount(totalMentors);
    
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      setCurrentStep(3);
    }
  };

  const handleCheckout = async () => {
    try {
      setSubmitting(true);
      
      const checkoutData = {
        uid: user.uid,
        planType: selectedPlan,
        mentorCount: mentorCount,
        existingMentors: selectedMentors,
        newMentors: newMentors,
        isRenewal: true
      };

      console.log('OrganizationRenewal: Starting checkout process:', checkoutData);

      const response = await authFetch(`${backendConfig.baseUrl}/api/membership/renewal-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(checkoutData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Checkout failed');
      }

      const result = await response.json();
      console.log('OrganizationRenewal: Checkout successful:', result);

      // Redirect to success page or dashboard
      navigate('/organization-dashboard', { 
        state: { 
          renewalSuccess: true, 
          message: 'Organization membership renewed successfully! Access codes have been sent to all mentors.' 
        }
      });

    } catch (err) {
      console.error('OrganizationRenewal: Checkout error:', err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="art-premium-page-bg">
      <ArtmosphereNavbar />
      
      <div className="relative z-10 mx-auto max-w-4xl px-4 py-8">
        <div className="art-premium-surface p-6 sm:p-8">
          {initialLoading && (
            <div className="mb-4 art-callout-strip px-4 py-3 text-[#5c4428] text-sm">
              Loading existing mentors...
            </div>
          )}
          <div className="text-center mb-8">
            <h1 className="art-premium-heading mb-2 text-3xl">Renew Organization Membership</h1>
            <p className="text-[#8b6914]">Reactivate your organization and restore mentor access</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Step 1: Select Mentors */}
          {currentStep === 1 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Step 1: Select Mentors</h2>
              
              {/* Existing Mentors */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Existing Mentors</h3>
                {existingMentors.length > 0 ? (
                  <div className="space-y-3">
                    {existingMentors.map(mentor => (
                      <div key={mentor.id} className="flex items-center p-4 border border-[#F5D99B]/55 rounded-lg bg-[#FFF9EF]">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedMentors.some(m => m.id === mentor.id)}
                            onChange={(e) => handleMentorToggle(mentor.id, e.target.checked)}
                            className="mr-3 h-4 w-4 text-[#8b6914] focus:ring-amber-500 border-amber-300 rounded"
                          />
                          <div>
                            <p className="font-medium text-[#2f251b]">{mentor.name}</p>
                            <p className="text-sm text-[#8b6914]">{mentor.email}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <p className="text-gray-600 mb-2">No existing mentors found for this account.</p>
                    <p className="text-sm text-gray-500">You can add new mentors below to get started.</p>
                  </div>
                )}
              </div>

              {/* Add New Mentors */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Add New Mentors</h3>
                  <button
                    onClick={handleAddNewMentor}
                    className="art-btn-primary px-4 py-2 rounded-lg transition-colors"
                  >
                    + Add Mentor
                  </button>
                </div>
                
                <div className="space-y-3">
                  {newMentors.map(mentor => (
                    <div key={mentor.id} className="flex items-center space-x-3 p-4 border border-[#F5D99B]/55 rounded-lg bg-[#FFF9EF]">
                      <input
                        type="text"
                        placeholder="Mentor Name"
                        value={mentor.name}
                        onChange={(e) => handleNewMentorChange(mentor.id, 'name', e.target.value)}
                        className="auth-premium-input flex-1 px-3 py-2"
                      />
                      <input
                        type="email"
                        placeholder="Mentor Email"
                        value={mentor.email}
                        onChange={(e) => handleNewMentorChange(mentor.id, 'email', e.target.value)}
                        className="auth-premium-input flex-1 px-3 py-2"
                      />
                      <button
                        onClick={() => handleRemoveNewMentor(mentor.id)}
                        className="text-red-600 hover:text-red-800 px-3 py-2"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleNextStep}
                  disabled={selectedMentors.length + newMentors.length === 0}
                  className="art-btn-primary px-6 py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Continue to Plans ({selectedMentors.length + newMentors.length} mentor{selectedMentors.length + newMentors.length !== 1 ? 's' : ''})
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Choose Plan */}
          {currentStep === 2 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Step 2: Choose Your Plan</h2>
              <p className="text-gray-600 mb-6">Select a plan for {mentorCount} mentor{mentorCount !== 1 ? 's' : ''}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {Object.entries(mentorPlans).map(([planKey, plan]) => (
                  <div
                    key={planKey}
                    className={`art-select-card cursor-pointer p-6 transition-all ${
                      selectedPlan === planKey ? 'art-select-card--selected' : ''
                    }`}
                    onClick={() => setSelectedPlan(planKey)}
                  >
                    <div className="text-center">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                      <div className="text-3xl font-bold text-[#8b6914] mb-2">
                        ₹{plan.totalPrice.toLocaleString()}
                      </div>
                      <div className="text-sm text-[#8b6914] mb-4">
                        ₹{plan.perMentorPrice}/mentor/month
                      </div>
                      
                      <ul className="text-sm text-gray-600 space-y-2 mb-6">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-center">
                            <span className="mr-2 text-emerald-600">✓</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="art-btn-secondary px-6 py-3 rounded-lg transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleNextStep}
                  className="art-btn-primary px-6 py-3 rounded-lg transition-colors"
                >
                  Continue to Checkout
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Checkout */}
          {currentStep === 3 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Step 3: Complete Renewal</h2>
              
              <div className="art-callout-strip rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Summary</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Plan: {mentorPlans[selectedPlan].name}</span>
                    <span>₹{mentorPlans[selectedPlan].totalPrice.toLocaleString()}/month</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Mentors: {mentorCount}</span>
                    <span>₹{mentorPlans[selectedPlan].perMentorPrice}/mentor</span>
                  </div>
                  <div className="border-t pt-2 mt-4">
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total Monthly</span>
                      <span>₹{mentorPlans[selectedPlan].totalPrice.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="art-callout-strip mb-6 p-4">
                <h4 className="font-semibold text-[#6b5a48] mb-2">What happens next:</h4>
                <ul className="text-sm text-[#8b6914] space-y-1">
                  <li>• Existing mentors will receive reactivated access codes</li>
                  <li>• New mentors will receive access codes and organization code</li>
                  <li>• All mentors will regain access to their dashboards</li>
                  <li>• Organization tools will be reactivated</li>
                </ul>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="art-btn-secondary px-6 py-3 rounded-lg transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleCheckout}
                  disabled={submitting}
                  className="art-btn-primary px-6 py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? 'Processing...' : 'Complete Renewal'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}